import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Popper,
  Stack
} from '@mui/material';
import { AddOutlined as AddIcon } from '@mui/icons-material';
import { listCollections } from '../../../services/api';
import { builderOutlinedControlSx } from '../../shared/markdownBodySx';

const CollectionSelector = ({ 
  selectedCollectionId, 
  onCollectionChange, 
  userProfile,
  disabled = false,
  onCreateCollection,
  refreshKey = 0,
  /** When true, do not show label or icon inside the input (parent provides header) */
  compact = false,
  /** When true, do not render the "Create New Project" button (parent provides + control) */
  hideCreateButton = false,
  /** Input size: 'small' | 'medium' - matches Select height when compact */
  size = 'medium',
  dropdownMinWidth = null
}) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load user's collections and team/company collections they have access to
  useEffect(() => {
    const loadCollections = async () => {
      if (!userProfile?.companyId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Load user's personal collections, team collections, and company collections
        // Company scope already includes all collections the user has access to via PermissionEngine
        // This includes collections with custom permissions (interact or owner)
        const [userResponse, teamResponse, companyResponse] = await Promise.all([
          listCollections('user'),
          listCollections('team'),
          listCollections('company')
        ]);
        
        // Combine all collections and remove duplicates
        const allCollections = [
          ...(userResponse.collections || []),
          ...(teamResponse.collections || []),
          ...(companyResponse.collections || [])
        ];
        
        // Remove duplicates based on collection ID
        const uniqueCollections = allCollections.filter((collection, index, self) => 
          index === self.findIndex(c => c.id === collection.id)
        );
        
        setCollections(uniqueCollections);
      } catch (err) {
        console.error('Error loading collections:', err);
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    loadCollections();
  }, [userProfile?.companyId, refreshKey]);

  // Filter collections based on search term
  const filteredCollections = useMemo(() => {
    if (!searchTerm.trim()) {
      // When no search term, show user's own collections first, then others
      return collections.sort((a, b) => {
        const aIsUserOwned = a.userId === userProfile?.id;
        const bIsUserOwned = b.userId === userProfile?.id;
        if (aIsUserOwned && !bIsUserOwned) return -1;
        if (!aIsUserOwned && bIsUserOwned) return 1;
        return a.title.localeCompare(b.title);
      });
    }
    
    // When searching, filter by title, description, or tags
    const searchLower = searchTerm.toLowerCase();
    return collections.filter(collection => 
      collection.title.toLowerCase().includes(searchLower) ||
      (collection.description && collection.description.toLowerCase().includes(searchLower)) ||
      (collection.tags && collection.tags.some(tag => tag.toLowerCase().includes(searchLower)))
    );
  }, [collections, searchTerm, userProfile?.id]);

  const handleChange = (event, newValue) => {
    if (newValue) {
      onCollectionChange({
        collectionId: newValue.id,
        collectionName: newValue.title
      });
    } else {
      onCollectionChange({
        collectionId: null,
        collectionName: null
      });
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setSearchTerm(newInputValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading projects...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const selectedCollection = collections.find(c => c.id === selectedCollectionId);

  return (
    <Box>
      <Autocomplete
        fullWidth
        disabled={disabled}
        PopperComponent={(props) => (
          <Popper
            {...props}
            sx={{
              ...props.sx,
              minWidth: dropdownMinWidth || 640,
              maxWidth: 'calc(100vw - 32px)',
              '& .MuiAutocomplete-option, & .MuiMenuItem-root, & .MuiTypography-root': {
                color: '#111827',
              },
            }}
          />
        )}
        options={filteredCollections}
        value={selectedCollection || null}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={(option) => option.title || ''}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        renderInput={(params) => (
          <TextField
            {...params}
            size={size}
            label={compact ? undefined : 'Project'}
            placeholder={compact ? 'Select or search projects...' : 'Search projects or start typing...'}
            sx={compact ? {
              ...builderOutlinedControlSx,
              '& .MuiInputBase-input': { fontSize: '0.875rem' },
              '& .MuiInputBase-input::placeholder': { color: 'text.secondary', opacity: 1 },
            } : undefined}
            InputProps={params.InputProps}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ color: '#111827', alignItems: 'flex-start !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', minWidth: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: '#111827', fontWeight: 650, minWidth: 0 }} noWrap>
                    {option.title}
                  </Typography>
                  {option.userId === userProfile?.id && (
                    <Chip
                      label="My Project"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
                    />
                  )}
                </Stack>
                {option.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#111827',
                      opacity: 0.78,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.35,
                    }}
                  >
                    {option.description}
                  </Typography>
                )}
              </Box>
              {option.promptCount > 0 && (
                <Chip 
                  label={option.promptCount} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        )}
        noOptionsText={
          searchTerm.trim() 
            ? `No projects found matching "${searchTerm}"`
            : "No projects available"
        }
        clearOnEscape
        selectOnFocus
        handleHomeEndKeys
      />
      {onCreateCollection && !hideCreateButton && (
        <Button
          startIcon={<AddIcon />}
          onClick={onCreateCollection}
          disabled={disabled}
          sx={{ mt: 1.5 }}
          size="small"
          variant="outlined"
        >
          Create New Project
        </Button>
      )}
    </Box>
  );
};

export default CollectionSelector;
